import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminScripts.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AdminScripts() {
  const [scripts, setScripts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    platform: 'fivem',
    frameworks: [],
    features: '',
    dependencies: '',
    price: '',
    tebex_url: '',
    preview_url: ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scripts`);
      setScripts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching scripts:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'frameworks') {
        formDataToSend.append('framework', formData.frameworks.join(','));
      } else {
        formDataToSend.append(key, formData[key]);
      }
    });
    if (thumbnailFile) {
      formDataToSend.append('thumbnail', thumbnailFile);
    }

    try {
      if (editingScript) {
        await axios.put(
          `${API_URL}/api/scripts/${editingScript.id}`,
          formDataToSend,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/scripts`,
          formDataToSend,
          { withCredentials: true }
        );
      }
      resetForm();
      fetchScripts();
    } catch (error) {
      console.error('Error saving script:', error);
    }
  };

  const handleEdit = (script) => {
    setEditingScript(script);
    const scriptFrameworks = script.framework ? script.framework.split(',') : [];
    setFormData({
      title: script.title,
      description: script.description,
      platform: script.platform,
      frameworks: scriptFrameworks,
      features: script.features || '',
      dependencies: script.dependencies || '',
      price: script.price || '',
      tebex_url: script.tebex_url || '',
      preview_url: script.preview_url || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this script?')) {
      try {
        await axios.delete(`${API_URL}/api/scripts/${id}`, {
          withCredentials: true
        });
        fetchScripts();
      } catch (error) {
        console.error('Error deleting script:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      platform: 'fivem',
      frameworks: [],
      features: '',
      dependencies: '',
      price: '',
      tebex_url: '',
      preview_url: ''
    });
    setThumbnailFile(null);
    setEditingScript(null);
    setShowForm(false);
  };

  const handleFrameworkToggle = (framework) => {
    setFormData(prev => {
      const frameworks = prev.frameworks.includes(framework)
        ? prev.frameworks.filter(f => f !== framework)
        : [...prev.frameworks, framework];
      return { ...prev, frameworks };
    });
  };

  const getAvailableFrameworks = () => {
    if (formData.platform === 'redm') {
      return ['RSG', 'VORP', 'Standalone'];
    }
    return ['ESX', 'QB', 'QBX', 'Standalone'];
  };

  const handleFeaturesChange = (e) => {
    const value = e.target.value;
    
    // If starting from empty, add emoji
    if (!formData.features && value && !value.startsWith('🔹')) {
      setFormData({ ...formData, features: '🔹 ' + value });
      return;
    }
    
    const lines = value.split('\n');
    
    // Add emoji to new lines that don't have it
    const updatedLines = lines.map((line, index) => {
      if (line.trim() && !line.trim().startsWith('🔹')) {
        return '🔹 ' + line.trim();
      }
      return line;
    });
    
    setFormData({ ...formData, features: updatedLines.join('\n') });
  };

  const handleFeaturesKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const cursorPos = e.target.selectionStart;
      const textBefore = formData.features.substring(0, cursorPos);
      const textAfter = formData.features.substring(cursorPos);
      setFormData({ 
        ...formData, 
        features: textBefore + '\n🔹 ' + textAfter 
      });
      // Set cursor position after the emoji
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = cursorPos + 3;
      }, 0);
    }
  };

  return (
    <div className="admin-scripts">
      <div className="panel-header">
        <div>
          <h1>Manage Scripts</h1>
          <p>Add, edit, or remove scripts from your store</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            Add New Script
          </button>
        )}
      </div>

      {showForm && (
        <div className="script-form glass fade-in">
          <h2>{editingScript ? 'Edit Script' : 'Add New Script'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="Brief description (optional)"
              />
            </div>

            <div className="form-group">
              <label>Features *</label>
              <textarea
                value={formData.features}
                onChange={handleFeaturesChange}
                onKeyDown={handleFeaturesKeyDown}
                required
                rows="6"
                placeholder="Press Enter for new feature (🔹 will be added automatically)"
              />
              <small>Each line will automatically start with 🔹 emoji</small>
            </div>

            <div className="form-group">
              <label>Dependencies</label>
              <textarea
                value={formData.dependencies}
                onChange={(e) => setFormData({ ...formData, dependencies: e.target.value })}
                rows="3"
                placeholder="List dependencies (one per line)"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Platform *</label>
                <select
                  value={formData.platform}
                  onChange={(e) => {
                    setFormData({ ...formData, platform: e.target.value, frameworks: [] });
                  }}
                  required
                >
                  <option value="fivem">FiveM</option>
                  <option value="redm">RedM</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Leave empty for free"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Frameworks</label>
              <div className="framework-checkboxes">
                {getAvailableFrameworks().map(fw => (
                  <label key={fw} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.frameworks.includes(fw)}
                      onChange={() => handleFrameworkToggle(fw)}
                    />
                    <span>{fw === 'QB' ? 'QB-Core' : fw}</span>
                  </label>
                ))}
              </div>
              <small>Select all frameworks this script supports</small>
            </div>

            <div className="form-group">
              <label>Tebex URL</label>
              <input
                type="url"
                value={formData.tebex_url}
                onChange={(e) => setFormData({ ...formData, tebex_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="form-group">
              <label>Preview Video URL</label>
              <input
                type="url"
                value={formData.preview_url}
                onChange={(e) => setFormData({ ...formData, preview_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
              />
              <small>Link to video preview (YouTube, Vimeo, etc.)</small>
            </div>

            <div className="form-group">
              <label>Thumbnail Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnailFile(e.target.files[0])}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingScript ? 'Update Script' : 'Create Script'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="scripts-list">
        {loading ? (
          <div className="loading">Loading scripts...</div>
        ) : scripts.length === 0 ? (
          <div className="empty-state glass">
            <div className="empty-icon">📦</div>
            <h3>No Scripts Yet</h3>
            <p>Start by adding your first script!</p>
          </div>
        ) : (
          <div className="scripts-grid">
            {scripts.map((script) => (
              <div key={script.id} className="script-card glass fade-in">
                {script.thumbnail && (
                  <div className="script-thumbnail">
                    <img src={`${API_URL}${script.thumbnail}`} alt={script.title} />
                  </div>
                )}
                <div className="script-content">
                  <h3>{script.title}</h3>
                  <p>{script.description}</p>
                  <div className="script-meta">
                    <span className={`platform-badge ${script.platform}`}>
                      {script.platform === 'both' ? 'FiveM & RedM' : script.platform.toUpperCase()}
                    </span>
                    <span className="script-price">
                      {script.price ? `$${script.price}` : 'Free'}
                    </span>
                  </div>
                  <div className="script-actions">
                    <button onClick={() => handleEdit(script)} className="btn btn-secondary">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(script.id)} className="btn btn-danger">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminScripts;
